import type { LearningDimensionRatios, LearningLayer, SkillDimension } from '@/types'

const dimensions: SkillDimension[] = ['listening', 'speaking', 'reading', 'writing']

const baselineRatios: Record<LearningLayer, LearningDimensionRatios> = {
  A: { listening: 20, speaking: 25, reading: 30, writing: 25 },
  B: { listening: 30, speaking: 25, reading: 25, writing: 20 },
  C: { listening: 40, speaking: 30, reading: 20, writing: 10 },
}

export function getBaselineLearningRatios(layer: LearningLayer): LearningDimensionRatios {
  return { ...baselineRatios[layer] }
}

const dimensionLabels: Record<SkillDimension, string> = {
  listening: '听力',
  speaking: '口语',
  reading: '阅读',
  writing: '写作',
}

export interface LearningRatioResult {
  ratios: LearningDimensionRatios
  reason: string
}

const targetScoreByLayer: Record<LearningLayer, number> = {
  A: 85,
  B: 75,
  C: 65,
}

function normalizeRatioWeights(weights: Record<SkillDimension, number>): LearningDimensionRatios {
  const totalWeight = dimensions.reduce((sum, dimension) => sum + Math.max(0, weights[dimension]), 0)
  if (totalWeight <= 0) return getBaselineLearningRatios('B')

  const exact = dimensions.map((dimension, index) => ({
    dimension,
    index,
    value: (Math.max(0, weights[dimension]) / totalWeight) * 100,
  }))
  const ratios = Object.fromEntries(exact.map(({ dimension, value }) => [
    dimension,
    Math.floor(value),
  ])) as LearningDimensionRatios
  let remaining = 100 - Object.values(ratios).reduce((sum, value) => sum + value, 0)

  exact
    .sort((left, right) => (
      (right.value - Math.floor(right.value)) - (left.value - Math.floor(left.value))
      || left.index - right.index
    ))
    .forEach(({ dimension }) => {
      if (remaining <= 0) return
      ratios[dimension] += 1
      remaining -= 1
    })

  return ratios
}

/**
 * Converts actual four-dimension scores into the next stage task mix.
 * Missing dimensions stay neutral instead of being treated as a score of zero.
 */
export function calculateLearningRatiosFromScores(
  layer: LearningLayer,
  scores: Partial<Record<SkillDimension, number>>,
  counts: Partial<Record<SkillDimension, number>> = {},
): LearningRatioResult {
  const baseline = getBaselineLearningRatios(layer)
  const target = targetScoreByLayer[layer]
  const measured = dimensions.filter((dimension) => (
    Number.isFinite(scores[dimension]) && (counts[dimension] ?? 1) > 0
  ))

  if (measured.length === 0) {
    return { ratios: baseline, reason: `当前阶段四维有效样本不足，沿用 ${layer} 层基础配比。` }
  }

  const weights = { ...baseline }
  for (const dimension of dimensions) {
    if (!measured.includes(dimension)) continue
    const score = Math.max(0, Math.min(100, scores[dimension] ?? target))
    const deficit = Math.max(0, target - score) / Math.max(1, target)
    const surplus = Math.max(0, score - target) / Math.max(1, 100 - target)
    const factor = Math.max(0.7, Math.min(1.7, 1 + deficit * 0.8 - surplus * 0.25))
    weights[dimension] = baseline[dimension] * factor
  }

  const ratios = normalizeRatioWeights(weights)
  const ordered = [...measured].sort((left, right) => (
    (scores[left] ?? target) - (scores[right] ?? target)
  ))
  const scoreText = ordered
    .map((dimension) => `${dimensionLabels[dimension]} ${Math.round(scores[dimension] ?? 0)} 分`)
    .join('、')

  return {
    ratios,
    reason: `以 ${layer} 层基础配比为起点，根据本阶段${scoreText}的相对差距分配下一阶段题量。`,
  }
}

export function calculateLearningRatios(
  layer: LearningLayer,
  supportDimensions: readonly SkillDimension[] = [],
  strongDimensions: readonly SkillDimension[] = [],
): LearningRatioResult {
  const ratios = getBaselineLearningRatios(layer)
  const support = [...new Set(supportDimensions)].slice(0, 2)

  if (support.length === 0) {
    return { ratios, reason: `沿用 ${layer} 层听说读写基线配比。` }
  }

  const boostPerDimension = support.length === 1 ? 10 : 5
  const totalBoost = boostPerDimension * support.length
  support.forEach((dimension) => { ratios[dimension] += boostPerDimension })

  const donors = [
    ...new Set(strongDimensions.filter((dimension) => !support.includes(dimension))),
    ...dimensions
      .filter((dimension) => !support.includes(dimension) && !strongDimensions.includes(dimension))
      .sort((left, right) => ratios[right] - ratios[left]),
  ]
  let remaining = totalBoost
  for (const dimension of donors) {
    if (remaining === 0) break
    const reduction = Math.min(5, remaining, ratios[dimension] - 5)
    ratios[dimension] -= reduction
    remaining -= reduction
  }

  return {
    ratios,
    reason: `在 ${layer} 层基线配比上，将${support.map((dimension) => dimensionLabels[dimension]).join('、')}各上调 ${boostPerDimension}%，用于优先巩固动态评估识别出的薄弱方向。`,
  }
}
